import { useState } from 'react';

export interface PaymentData {
  method: 'card' | 'sbp' | 'cash' | 'bonus';
  cardNumber: string;
  cardName: string;
  cardExpiry: string;
  cardCvv: string;
  promoCode: string;
  promoApplied: boolean;
  useBonus: boolean;
  bonusAmount: number;
}

interface Props {
  data: PaymentData;
  onChange: (data: PaymentData) => void;
  onNext: () => void;
  onBack: () => void;
  totalPrice: number;
}

const PAYMENT_METHODS = [
  { id: 'card' as const, label: 'Банковская карта', desc: 'Visa, Mastercard, МИР', icon: 'ri-bank-card-line', color: '#ff2d78' },
  { id: 'sbp' as const, label: 'СБП', desc: 'Система быстрых платежей', icon: 'ri-qr-code-line', color: '#00f5ff' },
  { id: 'cash' as const, label: 'Наличными', desc: 'При получении заказа', icon: 'ri-money-cny-circle-line', color: '#39ff14' },
  { id: 'bonus' as const, label: 'Бонусами', desc: 'Списать бонусные баллы', icon: 'ri-vip-crown-line', color: '#f0ff00' },
];

const VALID_CODES = ['VIT40', 'HEART30', 'FIRST500', 'FREERUN', 'COLD25', 'SKIN20', 'BONUS2X', 'STOMACH15'];
const USER_BONUS = 3248;

const formatCard = (v: string) => v.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
const formatExpiry = (v: string) => {
  const d = v.replace(/\D/g, '').slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
};

const PaymentForm = ({ data, onChange, onNext, onBack, totalPrice }: Props) => {
  const [promoInput, setPromoInput] = useState(data.promoCode);
  const [promoStatus, setPromoStatus] = useState<'idle' | 'ok' | 'err'>('idle');
  const set = (key: keyof PaymentData) => (val: string | boolean | number) =>
    onChange({ ...data, [key]: val });

  const applyPromo = () => {
    if (VALID_CODES.includes(promoInput.trim().toUpperCase())) {
      onChange({ ...data, promoCode: promoInput.trim().toUpperCase(), promoApplied: true });
      setPromoStatus('ok');
    } else {
      setPromoStatus('err');
      setTimeout(() => setPromoStatus('idle'), 2500);
    }
  };

  const bonusDiscount = data.useBonus ? Math.min(USER_BONUS, Math.floor(totalPrice * 0.99)) : 0;
  const finalPrice = Math.max(0, totalPrice - bonusDiscount);

  const canProceed = () => {
    if (data.method === 'card') {
      return data.cardNumber.replace(/\s/g, '').length === 16 && data.cardName.trim() && data.cardExpiry.length === 5 && data.cardCvv.length === 3;
    }
    return true;
  };

  return (
    <div className="space-y-6">
      {/* Payment method */}
      <div>
        <h3 className="font-orbitron font-bold text-sm text-white mb-4">Способ оплаты</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {PAYMENT_METHODS.map(m => (
            <button
              key={m.id}
              onClick={() => onChange({ ...data, method: m.id })}
              className="text-left p-4 rounded-xl cursor-pointer transition-all duration-200"
              style={{
                background: data.method === m.id ? `${m.color}10` : 'rgba(255,255,255,0.02)',
                border: data.method === m.id ? `1px solid ${m.color}50` : '1px solid rgba(255,255,255,0.07)',
                boxShadow: data.method === m.id ? `0 0 14px ${m.color}20` : 'none',
              }}
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-xl mb-3" style={{ background: `${m.color}15` }}>
                <i className={`${m.icon} text-base`} style={{ color: m.color }} />
              </div>
              <p className="font-orbitron font-bold text-xs text-white mb-0.5">{m.label}</p>
              <p className="font-mono text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>{m.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Card form */}
      {data.method === 'card' && (
        <div
          className="p-5 rounded-2xl space-y-4"
          style={{ background: 'rgba(255,45,120,0.04)', border: '1px solid rgba(255,45,120,0.12)' }}
        >
          <h4 className="font-orbitron font-bold text-xs text-white">Данные карты</h4>

          {/* Card preview */}
          <div
            className="relative h-36 rounded-2xl overflow-hidden p-5 flex flex-col justify-between"
            style={{ background: 'linear-gradient(135deg, #1a0a14, #0a0a1a)', border: '1px solid rgba(255,45,120,0.2)' }}
          >
            <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(circle at 70% 30%, #ff2d78, transparent 60%)' }} />
            <div className="flex items-center justify-between relative z-10">
              <span className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>NeonPharma Pay</span>
              <i className="ri-bank-card-line text-xl" style={{ color: 'rgba(255,45,120,0.6)' }} />
            </div>
            <div className="relative z-10">
              <p className="font-orbitron font-bold text-lg text-white tracking-widest mb-1">
                {data.cardNumber || '•••• •••• •••• ••••'}
              </p>
              <div className="flex items-center justify-between">
                <p className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  {data.cardName || 'ИМЯ ВЛАДЕЛЬЦА'}
                </p>
                <p className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  {data.cardExpiry || 'MM/YY'}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block font-mono text-xs mb-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>Номер карты *</label>
              <input
                type="text"
                value={data.cardNumber}
                onChange={e => set('cardNumber')(formatCard(e.target.value))}
                placeholder="0000 0000 0000 0000"
                className="w-full px-4 py-3 rounded-xl font-mono text-sm text-white outline-none tracking-widest"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                onFocus={e => { (e.target as HTMLElement).style.borderColor = 'rgba(255,45,120,0.4)'; }}
                onBlur={e => { (e.target as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; }}
              />
            </div>
            <div>
              <label className="block font-mono text-xs mb-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>Имя владельца *</label>
              <input
                type="text"
                value={data.cardName}
                onChange={e => set('cardName')(e.target.value.toUpperCase())}
                placeholder="IVAN IVANOV"
                className="w-full px-4 py-3 rounded-xl font-mono text-sm text-white outline-none tracking-wider"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                onFocus={e => { (e.target as HTMLElement).style.borderColor = 'rgba(255,45,120,0.4)'; }}
                onBlur={e => { (e.target as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; }}
              />
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block font-mono text-xs mb-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>Срок действия *</label>
                <input
                  type="text"
                  value={data.cardExpiry}
                  onChange={e => set('cardExpiry')(formatExpiry(e.target.value))}
                  placeholder="MM/YY"
                  className="w-full px-4 py-3 rounded-xl font-mono text-sm text-white outline-none"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                  onFocus={e => { (e.target as HTMLElement).style.borderColor = 'rgba(255,45,120,0.4)'; }}
                  onBlur={e => { (e.target as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; }}
                />
              </div>
              <div className="flex-1">
                <label className="block font-mono text-xs mb-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>CVV *</label>
                <input
                  type="password"
                  value={data.cardCvv}
                  onChange={e => set('cardCvv')(e.target.value.replace(/\D/g, '').slice(0, 3))}
                  placeholder="•••"
                  className="w-full px-4 py-3 rounded-xl font-mono text-sm text-white outline-none"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                  onFocus={e => { (e.target as HTMLElement).style.borderColor = 'rgba(255,45,120,0.4)'; }}
                  onBlur={e => { (e.target as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SBP */}
      {data.method === 'sbp' && (
        <div className="p-5 rounded-2xl text-center" style={{ background: 'rgba(0,245,255,0.04)', border: '1px solid rgba(0,245,255,0.12)' }}>
          <div className="w-24 h-24 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(0,245,255,0.08)', border: '1px solid rgba(0,245,255,0.2)' }}>
            <i className="ri-qr-code-line text-4xl" style={{ color: '#00f5ff' }} />
          </div>
          <p className="font-orbitron font-bold text-sm text-white mb-2">QR-код для оплаты</p>
          <p className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
            После подтверждения заказа вы получите QR-код для оплаты через приложение банка
          </p>
        </div>
      )}

      {/* Cash */}
      {data.method === 'cash' && (
        <div className="p-5 rounded-2xl" style={{ background: 'rgba(57,255,20,0.04)', border: '1px solid rgba(57,255,20,0.12)' }}>
          <div className="flex items-center gap-3 mb-3">
            <i className="ri-information-line text-lg" style={{ color: '#39ff14' }} />
            <p className="font-orbitron font-bold text-sm text-white">Оплата при получении</p>
          </div>
          <p className="font-mono text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Подготовьте точную сумму: <span className="text-white font-bold">{totalPrice.toLocaleString()} ₽</span>. Курьер не всегда имеет сдачу. Также можно оплатить картой через терминал курьера.
          </p>
        </div>
      )}

      {/* Bonus */}
      {data.method === 'bonus' && (
        <div className="p-5 rounded-2xl" style={{ background: 'rgba(240,255,0,0.04)', border: '1px solid rgba(240,255,0,0.12)' }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-orbitron font-bold text-sm text-white">Бонусный счёт</p>
              <p className="font-mono text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Доступно для списания</p>
            </div>
            <div className="text-right">
              <p className="font-orbitron font-black text-2xl" style={{ color: '#f0ff00' }}>{USER_BONUS.toLocaleString()}</p>
              <p className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>баллов</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-xl cursor-pointer" style={{ background: 'rgba(240,255,0,0.06)', border: '1px solid rgba(240,255,0,0.15)' }}
            onClick={() => set('useBonus')(!data.useBonus)}>
            <div className="w-5 h-5 rounded flex items-center justify-center" style={{ background: data.useBonus ? '#f0ff00' : 'transparent', border: `2px solid ${data.useBonus ? '#f0ff00' : 'rgba(255,255,255,0.2)'}` }}>
              {data.useBonus && <i className="ri-check-line text-xs" style={{ color: '#050508' }} />}
            </div>
            <span className="font-mono text-sm text-white">Списать {Math.min(USER_BONUS, Math.floor(totalPrice * 0.99)).toLocaleString()} баллов</span>
            <span className="font-mono text-xs ml-auto" style={{ color: '#f0ff00' }}>-{bonusDiscount.toLocaleString()} ₽</span>
          </div>
        </div>
      )}

      {/* Promo code */}
      <div>
        <h3 className="font-orbitron font-bold text-sm text-white mb-3">Промокод</h3>
        <div className="flex gap-2">
          <input
            type="text"
            value={promoInput}
            onChange={e => { setPromoInput(e.target.value.toUpperCase()); setPromoStatus('idle'); }}
            onKeyDown={e => e.key === 'Enter' && applyPromo()}
            placeholder="Введите промокод"
            className="flex-1 px-4 py-3 rounded-xl font-mono text-sm text-white outline-none tracking-widest"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: promoStatus === 'ok' ? '1px solid rgba(57,255,20,0.4)' : promoStatus === 'err' ? '1px solid rgba(255,45,120,0.4)' : '1px solid rgba(255,255,255,0.08)',
            }}
          />
          <button
            onClick={applyPromo}
            className="whitespace-nowrap px-5 py-3 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200"
            style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.1)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,45,120,0.12)'; (e.currentTarget as HTMLElement).style.color = '#ff2d78'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.6)'; }}
          >
            Применить
          </button>
        </div>
        {promoStatus === 'ok' && <p className="font-mono text-xs mt-1.5 flex items-center gap-1" style={{ color: '#39ff14' }}><i className="ri-check-line" /> Промокод применён!</p>}
        {promoStatus === 'err' && <p className="font-mono text-xs mt-1.5 flex items-center gap-1" style={{ color: '#ff2d78' }}><i className="ri-close-line" /> Промокод не найден</p>}
      </div>

      {/* Buttons */}
      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="whitespace-nowrap flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-mono text-sm cursor-pointer transition-all duration-200"
          style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.25)'; (e.currentTarget as HTMLElement).style.color = '#fff'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.1)'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; }}
        >
          <i className="ri-arrow-left-line" />
          Назад
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed()}
          className="whitespace-nowrap flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-orbitron font-bold text-sm cursor-pointer transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: '#ff2d78', color: '#fff', boxShadow: '0 0 20px rgba(255,45,120,0.4)' }}
          onMouseEnter={e => { if (canProceed()) (e.currentTarget as HTMLElement).style.boxShadow = '0 0 30px rgba(255,45,120,0.6)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 0 20px rgba(255,45,120,0.4)'; }}
        >
          Подтвердить заказ
          <i className="ri-arrow-right-line" />
        </button>
      </div>
    </div>
  );
};

export default PaymentForm;
